import { useEffect, useMemo, useRef, useState } from "react";
import { useDataChangedTick } from "../useDataChanged";
import { IconChat, IconChevronLeft } from "./icons";
import { CardSkeleton } from "./Skeletons";
import {
  api,
  isActiveScanStatus,
  isNarrationEnabled,
  type NarratedBubble,
  type ScanEvent,
} from "../api";
import { useAppState } from "../AppStateContext";
import { demoStore } from "../demo/demoStore";
import { useI18n } from "../i18n";
import {
  FEED_KINDS,
  eventToNarrateItem,
  fallbackBubble,
  type FallbackLabels,
  type FeedBubble,
} from "./chatNarration";
import "./ChatPanel.css";

// The live feed is reconstructed from the scan event stream (`/scans/{id}/events`).
// We pull a curated set of high-signal kinds (findings, agent reasoning, exploit
// actions, milestones — see FEED_KINDS) and turn each into a punchy bubble.
//
// Two layers render the bubble:
//   • Layer 1 (fallbackBubble): deterministic, localized, instant, no network.
//   • Layer 2 (api.narrate → Cloudflare Worker → DeepSeek): rewrites the raw
//     text into manager-facing narration. Overlays Layer 1 by event_id when it
//     lands; if the worker is unset (VITE_NARRATE_URL) or fails, Layer 1 stands.
const PAGE_SIZE = 40; // newest feed events fetched per poll (tail=true)
const VISIBLE = 30; // how many of the latest to render
const NARRATE_BATCH = 20; // max events sent to the worker per tick (cost guard)
const POLL_INTERVAL_MS = 5_000;

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Chronological order (oldest → newest) so the feed reads like a transcript. */
function bySeqAsc(a: ScanEvent, b: ScanEvent): number {
  const ta = new Date(a.timestamp).getTime();
  const tb = new Date(b.timestamp).getTime();
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  const na = Number(a.sequence_number);
  const nb = Number(b.sequence_number);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return (a.sequence_number ?? "").localeCompare(b.sequence_number ?? "");
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n).replace(/\s+\S*$/, "") + "…" : s;
}

export function ChatPanel({
  open,
  onToggle,
}: {
  open?: boolean;
  onToggle?: () => void;
}) {
  const { viewedScan } = useAppState();
  const { t, lang } = useI18n();
  const scanId = viewedScan?.scan_id ?? null;
  const dataTick = useDataChangedTick();
  const isActive = isActiveScanStatus(viewedScan?.status);

  const [events, setEvents] = useState<ScanEvent[] | null>(null);
  const [narrated, setNarrated] = useState<Map<string, NarratedBubble>>(new Map());
  const [error, setError] = useState(false);
  // event_ids we've already sent to the worker — never narrate the same twice.
  const requestedRef = useRef<Set<string>>(new Set());
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // In the demo the chat opens on a backlog of events; reveal them one at a
  // time (oldest first) so the user watches the conversation arrive instead of
  // seeing a wall of text. Outside the demo, show everything at once.
  const [streamN, setStreamN] = useState(0);
  const streaming = open && demoStore.active;
  useEffect(() => {
    if (!open || !demoStore.active) {
      // Reset so the next open streams again from the top.
      const id0 = window.setTimeout(() => setStreamN(0), 0);
      return () => window.clearTimeout(id0);
    }
    const id = window.setInterval(() => setStreamN((n) => n + 1), 650);
    return () => window.clearInterval(id);
  }, [open]);

  // Keep the feed pinned to the newest bubble as messages stream in.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events, narrated, open, streamN]);

  const labels = useMemo<FallbackLabels>(
    () => ({
      planning: t("chat.planning"),
      structured: t("chat.structuredStep"),
      findingCritical: t("chat.findingCritical"),
      findingHigh: t("chat.findingHigh"),
      findingMedium: t("chat.findingMedium"),
      findingLow: t("chat.findingLow"),
      findingInfo: t("chat.findingInfo"),
      exploit: t("chat.exploit"),
      step: t("chat.step"),
      milestone: t("chat.milestone"),
    }),
    [t],
  );

  // Switching scans clears the previous feed. Deferred to dodge the
  // set-state-in-effect lint rule (repo idiom — see HypotesesPage).
  useEffect(() => {
    const id = window.setTimeout(() => {
      setEvents(null);
      setNarrated(new Map());
      setError(false);
      requestedRef.current = new Set();
    }, 0);
    return () => window.clearTimeout(id);
  }, [scanId]);

  // Poll the newest feed while the panel is open. `tail=true` returns the latest
  // page (fixes the old bug where a long scan showed only its stale first page).
  // Once the scan is terminal `isActive` flips false → one final fetch, no loop.
  useEffect(() => {
    if (!open || !scanId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await api.scanEvents(scanId, {
          kinds: FEED_KINDS,
          page_size: PAGE_SIZE,
          tail: true,
        });
        if (cancelled) return;
        setEvents((prev) => {
          const merged = new Map((prev ?? []).map((e) => [e.event_id, e]));
          for (const e of res.events) merged.set(e.event_id, e);
          return [...merged.values()].sort(bySeqAsc);
        });
        setError(false);

        // Layer 2: rewrite the events we haven't narrated yet.
        if (isNarrationEnabled()) {
          const items = res.events
            .filter((e) => !requestedRef.current.has(e.event_id))
            .flatMap((e) => {
              const it = eventToNarrateItem(e);
              return it ? [it] : [];
            })
            .slice(-NARRATE_BATCH);
          if (items.length > 0) {
            for (const it of items) requestedRef.current.add(it.id);
            try {
              const { results } = await api.narrate(items, lang);
              if (cancelled) return;
              setNarrated((prev) => {
                const next = new Map(prev);
                for (const r of results) if (r?.id) next.set(r.id, r);
                return next;
              });
            } catch {
              // Worker down / disabled mid-flight → Layer 1 keeps rendering.
            }
          }
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    const initialId = window.setTimeout(() => void tick(), 0);
    const timer = isActive ? window.setInterval(tick, POLL_INTERVAL_MS) : undefined;
    return () => {
      cancelled = true;
      window.clearTimeout(initialId);
      if (timer) window.clearInterval(timer);
    };
  }, [open, scanId, isActive, lang, dataTick]);

  if (!open) {
    return (
      <button
        type="button"
        className="chat chat--collapsed"
        data-tour="chat-rail"
        onClick={onToggle}
        aria-label={t("chat.open")}
      >
        <IconChat width={24} height={24} />
        <span className="chat__collapsed-label">{t("chat.collapsedLabel")}</span>
      </button>
    );
  }

  // Build the rendered bubbles: worker overlay wins, else the local fallback.
  // Events that neither layer can turn into a bubble are dropped.
  const rendered = (events ?? [])
    .slice(-VISIBLE)
    .flatMap((ev) => {
      const overlay = narrated.get(ev.event_id);
      const bubble: FeedBubble | null = overlay
        ? { emoji: overlay.emoji || "🧠", title: overlay.title, body: overlay.body }
        : fallbackBubble(ev, labels);
      if (!bubble || (!bubble.title && !bubble.body)) return [];
      return [{ id: ev.event_id, time: ev.timestamp, bubble }];
    });

  const renderBody = () => {
    if (!scanId) return <p className="chat__notice">{t("chat.noScan")}</p>;
    if (error && !events) return <p className="chat__notice">{t("chat.loadFailed")}</p>;
    if (!events) return <CardSkeleton count={4} />;
    if (rendered.length === 0) return <p className="chat__notice">{t("chat.emptyFeed")}</p>;
    const shown = streaming ? rendered.slice(0, Math.max(1, streamN)) : rendered;
    return (
      <div className="chat__narrative">
        {shown.map((r) => (
          <BubbleView key={r.id} bubble={r.bubble} time={r.time} />
        ))}
      </div>
    );
  };

  return (
    <aside className="chat chat--open" data-tour="chat-panel">
      <header className="chat__header">
        <div className="chat__title">
          <IconChat width={24} height={24} />
          <span>{t("chat.title")}</span>
        </div>
        <button
          type="button"
          className="chat__toggle"
          onClick={onToggle}
          aria-label={t("chat.close")}
        >
          <IconChevronLeft width={20} height={20} />
        </button>
      </header>

      <div className="chat__body" ref={bodyRef}>{renderBody()}</div>
    </aside>
  );
}

function BubbleView({ bubble, time }: { bubble: FeedBubble; time: string }) {
  return (
    <article className="narrative-msg">
      <p className="narrative-msg__title">
        <span aria-hidden className="narrative-msg__emoji">
          {bubble.emoji}
        </span>{" "}
        {bubble.title || truncate(bubble.body, 60)}
      </p>
      {bubble.title && bubble.body ? (
        <p className="narrative-msg__body">{truncate(bubble.body, 320)}</p>
      ) : null}
      <span className="narrative-msg__time">{formatTime(time)}</span>
    </article>
  );
}
